import { FC } from 'react';
import {
  StyledMultiEventIndicator,
  StyledMultiEventIndicators,
  StyledTimetableItem,
  StyledTimetableItemFooter,
  StyledTimetableItemLabel,
  StyledTimetableItemTag,
} from './TimetableItem.styles';
import { TimetableEvent } from '@versita/shared/models';

// This is a common format and is expected to be 'drilled down' via <Timetable>
export interface TimetableItemProps {
  /** a unique key for the item */
  id: string;
  /** the tag to show for the item */
  tag?: string;
  /** the label to show for the item */
  label: string;
  /** the start time of the item, ISO format */
  start: string;
  /** the end time of the item, ISO format */
  end: string;
  /** optional footer text to show for the item */
  footer?: string;
  /** the color to use for the item highlight */
  color?: string;
  /** details of the associated event */
  eventDetails: TimetableEvent;
  /** callback for when the item is clicked */
  onClick?: () => void;
}

// these props are an implementation detail and provided by the Timetable component
export interface CompleteTimetableItemProps extends TimetableItemProps {
  /** the top position of the item, as a percentage of the timetable height */
  topPositionPercent: number;
  /** the height of the item, as a percentage of the timetable height */
  heightPercent: number;
  /** the number of items that overlap with this item */
  overlaps: number;
  /** the horizontal index of this item in the list of overlapping items */
  horizontalIndex: number;
  /** the group id of the overlapping items */
  overlapGroupId: string | null;
  /** whether the item is a placeholder for multiple events that can't be shown at this zoom level */
  isMultiEvent?: boolean;
  subEvents?: CompleteTimetableItemProps[];
}

/**
A single timetable item
*/
export const TimetableItem: FC<CompleteTimetableItemProps> = ({
  topPositionPercent,
  heightPercent,
  overlaps,
  tag,
  horizontalIndex,
  color,
  label,
  footer,
  end,
  start,
  eventDetails,
  isMultiEvent,
  subEvents = [],
  ...otherProps
}) => {
  const width = 100 / (overlaps + 1);
  const offsetX = overlaps ? width * horizontalIndex : 0;
  return (
    <StyledTimetableItem
      {...otherProps}
      $topPosition={topPositionPercent}
      $height={heightPercent}
      $width={width}
      $offsetX={offsetX}
      $color={color}
      $isMultiEvent={isMultiEvent}
      title={isMultiEvent ? 'click to view all events' : label}
      aria-label={`Click to show more information about the event${
        isMultiEvent ? 's' : ''
      }`}
    >
      {!isMultiEvent && tag && (
        <StyledTimetableItemTag>{tag}</StyledTimetableItemTag>
      )}
      <StyledTimetableItemLabel
        $moduleCount={eventDetails.moduleNames.length}
        $floating={!!isMultiEvent}
      >
        {label}
      </StyledTimetableItemLabel>
      {isMultiEvent && (
        <StyledMultiEventIndicators>
          {subEvents.map((subEvent, i) => (
            <StyledMultiEventIndicator
              key={i}
              $topPosition={subEvent.topPositionPercent}
              $height={subEvent.heightPercent}
              $width={100 / subEvents.length}
              $offsetX={(100 / subEvents.length) * subEvent.horizontalIndex}
              $color={subEvent.color}
            />
          ))}
        </StyledMultiEventIndicators>
      )}
      {footer && (
        <StyledTimetableItemFooter>{footer}</StyledTimetableItemFooter>
      )}
    </StyledTimetableItem>
  );
};

export default TimetableItem;
